<script setup>
import { ref, computed } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { DxDropDownButton } from 'devextreme-vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import DataSource from 'devextreme/data/data_source'
import CustomStore from 'devextreme/data/custom_store'
import CheckBox from 'devextreme/ui/check_box'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import GenericPopup from '@/components/shared/GenericPopup.vue'
import RowImportGrid from '@/components/shared/RowImportGrid.vue'

import * as dataGrid from '@/composables/data-grid'

import { useCatalog } from '@/composables/catalog'
import { useClipboard } from '@/composables/clipboard'
import { useNotify } from '@/composables/notify'
import { useDialog } from '@/composables/dialog'
import { useImportRow } from '@/composables/import-row'
import { useImportRowValidation } from '@/composables/import-row-validation'
import { useImportRowApi, useImportRowWorkflow } from '@/composables/import-row-workflow'
import { useColumns as useValidationColumns } from '@/composables/sale/clipboard-row-grid'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

const props = defineProps({
  documentType: {
    type: Number,
    default: null,
  },
  documentId: {
    type: Number,
    default: null,
  },
  entityLock: {
    type: Object,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  displayedSources: {
    type: Array,
    default: null,
  },
  options: {
    type: Object,
    default: () => ({}),
  },
  hasExistingCarServiceProcessing: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['updated'])

let previousFocusedGrid = null
let isHiding = false
let releaseLockPromise = Promise.resolve()

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()

const { getAvailableCatalogItems } = useCatalog()
const { getClipboardItems } = useClipboard()
const { notifyError, notifySuccess } = useNotify()
const { customDialog } = useDialog()

const {
  isMove,
  isValidating,
  isImporting,
  reviewDataSource,
  blockingValidationCount,
  canImport,
  validateAndImportAsync,
  importAsync,
  replaceReviewRowAsync,
  setSelectedReviewKeys,
  reset,
} = useImportRowWorkflow(props)

const { importRowsAsync, revalidateRowAsync } = useImportRowApi(props)
const { validationColor, worstValidationType, rowValidationsTooltip } = useImportRowValidation()

const { canEditQuantity, useColumns } = useImportRow()
const rowImportGridColumns = useValidationColumns()

const gridRef = ref(null)

const gridId = 'import-row-grid'

const gridOptions = ref(null)
const selectedRows = ref(null)
const gridRows = ref([])
const currentSource = ref(null)
const step = ref(1)
const popupVisible = ref(false)
const groupImportRowsByDefault = ref(true)
const isSavingGroupPreference = ref(false)
let groupingInitialized = false

const currentUserDetail = computed(() => webSiteStore.userDetails.find(item => item.user_id === webSiteStore.user?.id))

const popupOptions = computed(() => ({
  title: i18n.t('common.importFrom'),
  maxHeight: '80%',
  width: '90%',
  saveButton: { hide: true },
  cancelButton: { hide: true },
  showCloseButton: !isImporting.value && !isValidating.value,
}))

const dropDownOptions = { width: 200 }

const dropDownItems = computed(() => {
  const items = [
    ...getAvailableCatalogItems(props.documentType),
    ...getClipboardItems(props.options),
  ]

  if (!props.displayedSources?.length) return items

  return items.filter(item => props.displayedSources.includes(item.source))
})

const hasSelection = computed(() => (selectedRows.value?.length ?? 0) > 0)
const hasExternalSourceSelected = computed(() =>
  selectedRows.value?.some(r => r.source !== webSiteStore.importRowTypesEnum.CLIPBOARD)
)

const isCopyDisabled = computed(() => !hasSelection.value || isValidating.value || hasExternalSourceSelected.value)
const isMoveDisabled = computed(() => !hasSelection.value || isValidating.value)

const groupSelectionMap = computed(() => {
  const selectedKeys = new Set((selectedRows.value || []).map(r => r.rowKey))
  const map = {}
  for (const row of gridRows.value) {
    const key = row.group_name ?? ''
    if (!map[key]) map[key] = { total: 0, selected: 0 }
    map[key].total++
    if (selectedKeys.has(row.rowKey)) map[key].selected++
  }
  return map
})

const groupState = (groupValue) => {
  const entry = groupSelectionMap.value[groupValue ?? '']
  if (!entry || !entry.total) return { value: false, count: 0 }
  if (entry.selected === 0) return { value: false, count: entry.total }
  if (entry.selected === entry.total) return { value: true, count: entry.total }
  return { value: null, count: entry.total }
}

const groupRowKeys = (groupValue) =>
  gridRows.value
    .filter(r => (r.group_name ?? '') === (groupValue ?? ''))
    .map(r => r.rowKey)

const toggleGroupSelection = (component, groupValue, checked) => {
  const keys = groupRowKeys(groupValue)
  if (!keys.length || !component) return
  if (checked) component.selectRows(keys, true)
  else component.deselectRows(keys)
}

const repaintGroupRows = (component) => {
  if (!component?.getVisibleRows) return
  const indexes = []
  component.getVisibleRows().forEach((row, index) => {
    if (row.rowType === 'group') indexes.push(index)
  })
  if (indexes.length) component.repaintRows(indexes)
}

const getSelectionColumns = () => [
  {
    caption: webSiteStore.blankCaption,
    dataField: 'validationStatus',
    cellTemplate: 'validationStatusCellTemplate',
    width: '2rem',
    alignment: 'center',
    allowEditing: false,
    allowFiltering: false,
    allowSorting: false,
    allowGrouping: false,
    visibleIndex: 0,
  },
  {
    caption: i18n.t('common.groupName'),
    dataField: 'group_name',
    width: '10rem',
    allowEditing: false,
    allowGrouping: true,
    groupIndex: groupImportRowsByDefault.value ? 0 : undefined,
    sortIndex: groupImportRowsByDefault.value ? undefined : 1,
    sortOrder: groupImportRowsByDefault.value ? undefined : 'asc',
    calculateGroupValue: (rowData) => rowData?.group_name ?? '',
    groupCellTemplate: 'groupNameGroupCellTemplate',
  },
  ...useColumns({ displayedSources: props.displayedSources }).map(col => ({
    ...col,
    ...(col.dataField === 'source' && !groupImportRowsByDefault.value ? { sortIndex: 0, sortOrder: 'desc' } : {}),
    allowGrouping: false,
    allowEditing: col.dataField === 'quantity' || col.dataField === 'keep_price_discount',
  })),
]

const applyRowsLayout = (component, grouped) => {
  if (!component) return

  component.beginUpdate()
  if (grouped) {
    component.columnOption('group_name', 'groupIndex', 0)
  } else {
    component.clearGrouping()
    component.clearSorting()
    component.columnOption('source', { sortIndex: 0, sortOrder: 'desc' })
    component.columnOption('group_name', { sortIndex: 1, sortOrder: 'asc' })
  }
  component.endUpdate()
}

const onGroupImportRowsChanged = async(grouped) => {
  const previousValue = groupImportRowsByDefault.value
  groupImportRowsByDefault.value = grouped
  applyRowsLayout(gridRef.value?.instance, grouped)
  isSavingGroupPreference.value = true

  try {
    await webSiteStore.updateImportGridPreferenceAsync(grouped)
  } catch {
    groupImportRowsByDefault.value = previousValue
    applyRowsLayout(gridRef.value?.instance, previousValue)
    notifyError(i18n.t('error.saving'))
  } finally {
    isSavingGroupPreference.value = false
  }
}

const onContentReady = (e) => {
  const component = e.component

  setTimeout(() => {
    if (!groupingInitialized) {
      groupingInitialized = true
      applyRowsLayout(component, groupImportRowsByDefault.value)
    }

    if (component.columnOption('source', 'filterValue') !== currentSource.value) {
      component.columnOption('source', 'filterValue', currentSource.value)
    }

    const isGroupedByName = component.columnOption('group_name', 'groupIndex') >= 0
    const hasEmptyGroup = gridRows.value.some(r => (r.group_name ?? '') === '')

    if (isGroupedByName && hasEmptyGroup && !component.isRowExpanded([''])) {
      component.expandRow([''])
    }
  }, 500)
}

const onEditorPreparing = (e) => {
  if (e.command === 'select') return

  if (e.parentType === 'dataRow' && e.dataField === 'quantity' && !canEditQuantity(e.row?.data)) {
    e.editorOptions.disabled = true
  }

  if (
    e.parentType === 'dataRow' &&
    e.dataField === 'keep_price_discount' &&
    e.row?.data?.source !== webSiteStore.importRowTypesEnum.CLIPBOARD
  ) {
    e.editorOptions.disabled = true
  }
}

const renderGroupSelectCheckbox = (rowElement, component, groupValue) => {
  const selectCell = rowElement?.querySelector?.('.dx-command-select')
  if (!selectCell) return

  selectCell.classList.remove('dx-cell-focus-disabled')
  selectCell.innerHTML = ''
  Object.assign(selectCell.style, { padding: '0', textAlign: 'center', verticalAlign: 'middle' })

  const state = groupState(groupValue)
  const container = document.createElement('div')
  selectCell.appendChild(container)

  new CheckBox(container, {
    value: state.value === null ? undefined : state.value,
    elementAttr: { class: 'dx-select-checkbox' },
    onValueChanged: (args) => {
      if (!args.event) return
      toggleGroupSelection(component, groupValue, !!args.value)
    },
  })
}

const onRowPrepared = (e) => {
  if (e.rowType === 'data') {
    if (e.data?.product_availability === registryStore.productAvailabilitiesEnum.NOT_AVAILABLE) {
      e.rowElement.classList.add('dx-disabled-row')
    }
    return
  }

  if (e.rowType !== 'group') return
  if ((e.data?.key ?? '') === '') {
    e.rowElement.style.display = 'none'
    return
  }
  renderGroupSelectCheckbox(e.rowElement, e.component, e.data?.key)
}

const onSaved = async(e, rows) => {
  for (const change of e.changes || []) {
    if (change.type !== 'update' || !('quantity' in change.data || 'keep_price_discount' in change.data)) {
      continue
    }

    const currentRow = rows.find(row => row.rowKey === change.key)
    if (!currentRow) continue

    const row = { ...currentRow, ...change.data }

    const updatedRow = await revalidateRowAsync(row, { quantity: row.quantity, keep_price_discount: row.keep_price_discount })

    if (!updatedRow) continue

    Object.assign(row, updatedRow)

    const rowIndex = e.component.getRowIndexByKey(change.key)
    if (rowIndex >= 0) e.component.repaintRows([rowIndex])
  }
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData.length ? e.selectedRowsData : null
  const component = e.component
  Promise.resolve().then(() => repaintGroupRows(component))
}

const onOptionChanged = (e) => {
  if (e.name === 'columns' && e.fullName?.endsWith('.filterValue')) {
    const newSource = e.component.columnOption('source', 'filterValue')
    if (newSource !== currentSource.value) {
      currentSource.value = newSource
      selectedRows.value = null
      e.component.getDataSource().reload()
    }
  }
}

const buildRows = (rawRows) =>
  rawRows.map((rawRow, index) => {
    const documentRowType = rawRow.document_row_type ?? webSiteStore.documentRowTypesEnum.PRODUCT

    return {
      ...rawRow,
      rowKey: `${rawRow.source}-${rawRow.id}-${index}`,
      source_type: webSiteStore.importRowSourceTypeMapping[rawRow.source],
      document_row_type: documentRowType,
      quantity: documentRowType === webSiteStore.documentRowTypesEnum.TEXT ? 0 : rawRow.quantity,
      keep_price_discount: rawRow.source === webSiteStore.importRowTypesEnum.CLIPBOARD,
    }
  })

const getServiceLabel = (source, catalogCode) => {
  const key = `enum.import_row_type.${source}`
  const translated = i18n.t(key)
  return translated !== key ? translated : catalogCode
}

const buildSelectionGridOptions = () => {
  let rows = []
  let isUpdating = false
  let hasError = false

  const dataSource = new DataSource({
    store: new CustomStore({
      key: 'rowKey',
      loadMode: 'raw',
      load: async() => {
        if (hasError) {
          return rows
        }
        if (!isUpdating) {
          try {
            const sources = currentSource.value != null ? [currentSource.value] : (props.displayedSources?.length ? props.displayedSources : null)
            const response = await importRowsAsync({
              documentType: props.documentType,
              documentId: props.documentId,
              ...(sources && { sources }),
              isDryRun: true,
              firstLoad: true,
              allowedDocumentRowTypes: props.options?.allowedDocumentRowTypes || null,
            })
            const payload = response?.data || {}
            rows = buildRows(payload.results || [])
            gridRows.value = rows

            const errors = payload.errors || []
            errors.forEach(({ source, catalog_code, message }) => {
              if (message) {
                notifyError(i18n.t(message))
                return
              }
              const serviceName = getServiceLabel(source, catalog_code)
              notifyError(i18n.t('error.service_unavailable', { service: serviceName }))
            })
          } catch (err) {
            hasError = true
            rows = []
            gridRows.value = []
            notifyError(i18n.t('error.catalog_rows_not_found'))
          }
        }
        isUpdating = false
        return rows
      },
      update: (key, values) => {
        isUpdating = true
        const row = rows.find(r => r.rowKey === key)
        if (row) Object.assign(row, values)
        return Promise.resolve(row)
      },
    }),
  })

  return dataGrid.useDefaultGridOptions({
    id: gridId,
    columns: getSelectionColumns(),
    dataSource,
    alreadyLocked: true,
    editing: { mode: 'cell', allowUpdating: true },
    entityLock: props.entityLock,
    entityLockReadOnlyDisabled: true,
    selection: { ...dataGrid.useDefaultGridSelection(), allowSelectAll: false },
    groupPanel: {
      visible: true,
      emptyPanelText: i18n.t('common.groupPanelPlaceholder', 2)
    },
    grouping: {
      contextMenuEnabled: true,
      autoExpandAll: false,
    },
    columnChooser: { enabled: false },
    onInitialized: (e) => dataGrid.useDefaultOnInitialized(e, { enableGrouping: true }),
    paging: { enabled: true },
    focusedRowEnabled: false,
    export: { enabled: false },
    remoteOperations: false,
    height: '50vh',
    onContentReady,
    onEditorPreparing,
    onRowPrepared,
    onSaved: (e) => onSaved(e, rows),
    onSelectionChanged,
    onOptionChanged,
  })
}

const onItemClick = async(e) => {
  await releaseLockPromise
  await props.entityLock?.lockEntity(props.documentId)
  previousFocusedGrid = dataGrid.focusedGrid.value
  currentSource.value = e.itemData.source
  groupImportRowsByDefault.value = currentUserDetail.value?.group_import_rows_by_default ?? true
  groupingInitialized = false
  gridOptions.value = buildSelectionGridOptions()
  popupVisible.value = true
}

const groupWorstValidationType = (groupValue) => {
  const key = groupValue ?? ''
  const validations = gridRows.value.filter(r => (r.group_name ?? '') === key).flatMap(r => r.validations || [])
  return worstValidationType(validations)
}

const onHiding = async() => {
  if (isHiding) return
  isHiding = true
  releaseLockPromise = props.entityLock?.releaseLock() ?? Promise.resolve()
  step.value = 1
  selectedRows.value = null
  currentSource.value = null
  groupingInitialized = false
  gridOptions.value = null
  gridRows.value = []
  reset()
  gridRef.value?.instance?.option('alreadyLocked', false)
  popupVisible.value = false
  if (previousFocusedGrid) {
    dataGrid.focusedGrid.value = previousFocusedGrid
    previousFocusedGrid = null
  }

  await releaseLockPromise
  isHiding = false
}

const finishImport = () => {
  notifySuccess(i18n.t('common.importSuccess'))
  onHiding()
  emit('updated')
}

const onNextButtonClick = async(isMoveRequest) => {
  const rows = selectedRows.value || []

  if (!rows.length) return

  await gridRef.value?.instance?.saveEditData()

  const hasAutodataRows = rows.some(row => row.source === webSiteStore.importRowTypesEnum.AUTODATA)
  let createNewAutodataProcessing = false
  if (hasAutodataRows && props.hasExistingCarServiceProcessing) {
    const addToExisting = await customDialog(
      i18n.t('common.importRow.autodataProcessingTitle'),
      i18n.t('common.importRow.autodataProcessingMessage'),
      i18n.t('common.importRow.addToExistingProcessing'),
      i18n.t('common.importRow.createNewProcessing'),
    )
    createNewAutodataProcessing = !addToExisting
  }

  const { status } = await validateAndImportAsync(
    rows,
    isMoveRequest,
    props.options?.allowedDocumentRowTypes || null,
    createNewAutodataProcessing,
  )

  if (status === 'imported') {
    finishImport()
    return
  }

  step.value = 2
}

const onImportClick = async() => {
  if (!canImport.value) return

  const { status } = await importAsync()

  if (status === 'imported') {
    finishImport()
    return
  }

  step.value = 2
  notifyError(i18n.t('error.importValidationError'))
}

const clearSelectionState = () => {
  selectedRows.value = null
  gridRef.value?.instance?.clearSelection()
}

const onBackClick = () => {
  clearSelectionState()
  step.value = 1
}
</script>

<template>
  <div>
    <dx-drop-down-button
      :disabled="props.disabled"
      :items="dropDownItems"
      :drop-down-options="dropDownOptions"
      :hint="i18n.t('common.importFrom')"
      display-expr="text"
      icon="mdi mdi-clipboard-arrow-left-outline mdi-18px"
      styling-mode="text"
      @item-click="onItemClick"
    />

    <generic-popup
      v-if="popupVisible"
      v-model:visible="popupVisible"
      :options="popupOptions"
      @cancel="onHiding"
      @hidden="onHiding"
    >
      <q-stepper
        v-model="step"
        color="primary"
        done-icon="mdi-check"
        done-color="positive"
        animated
        flat
      >
        <q-step
          :name="1"
          :title="i18n.t('common.selectRow', 2)"
          icon="mdi-book"
          :done="step > 1"
        >
          <div class="row justify-end q-mb-sm">
            <q-toggle
              :model-value="groupImportRowsByDefault"
              :label="i18n.t('common.groupImportRows')"
              :disable="isSavingGroupPreference"
              dense
              @update:model-value="onGroupImportRowsChanged"
            />
          </div>
          <dx-data-grid
            v-if="gridOptions"
            ref="gridRef"
            v-bind="gridOptions"
          >
            <template #importRowTypeCellTemplate="{ data: templateOptions }">
              <chip-status :data="webSiteStore.getLocalizedImportRowTypes().find(x => x.id === templateOptions.data.source)" />
            </template>

            <template #validationStatusCellTemplate="{ data }">
              <div
                :title="rowValidationsTooltip(data.data?.validations)"
                :style="{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  margin: '0 auto',
                  backgroundColor: validationColor(worstValidationType(data.data?.validations)),
                }"
              />
            </template>

            <template #groupNameGroupCellTemplate="{ data: groupCell }">
              <div class="row items-center no-wrap">
                <div
                  :style="{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    marginRight: '8px',
                    flexShrink: 0,
                    backgroundColor: validationColor(groupWorstValidationType(groupCell.value)),
                  }"
                />
                <span class="text-weight-medium">{{ groupCell.text }}</span>
              </div>
            </template>
            <template #documentRowTypeCellTemplate="{ data: templateOptions }">
              <chip-status :data="webSiteStore.getLocalizedDocumentRowTypes().find(x => x.id === templateOptions.data.document_row_type)" />
            </template>
          </dx-data-grid>
        </q-step>
        <q-step
          :name="2"
          :title="i18n.t('common.validationResult', 2)"
          icon="mdi-alert"
          color="warning"
          :disable="step < 2"
          :done="step > 2"
        >
          <row-import-grid
            style="max-height: 30rem;"
            :grid-id="`${gridId}-validation`"
            :columns="rowImportGridColumns"
            :data-source="reviewDataSource"
            :entity-lock="props.entityLock"
            :document-id="props.documentId"
            :document-type="props.documentType"
            :options="props.options"
            @update:selected-rows="replaceReviewRowAsync"
            @update:selected-keys="setSelectedReviewKeys"
          />
        </q-step>
        <template #navigation>
          <q-stepper-navigation class="column items-end">
            <div
              v-if="step > 1 && blockingValidationCount > 0"
              class="text-negative text-caption q-mb-sm"
            >
              {{ i18n.t('common.importRow.unresolvedIssues', { count: blockingValidationCount }) }}
            </div>
            <div>
              <q-btn
                v-if="step === 1"
                style="width: 9rem"
                dense
                class="q-ml-sm"
                :color="isValidating && !isMove ? 'warning' : 'primary'"
                :loading="isValidating && !isMove"
                :label="i18n.t('common.copy')"
                :disable="isCopyDisabled"
                @click="() => onNextButtonClick(false)"
              >
                <template #loading>
                  <div class="q-ph-md row">
                    <div>{{ i18n.t('common.validating') }}</div>
                    <q-spinner-dots class="on-right" />
                  </div>
                </template>
              </q-btn>
              <q-btn
                v-if="step === 1"
                style="min-width: 9rem"
                dense
                class="q-ml-sm"
                :color="isValidating && isMove ? 'warning' : 'primary'"
                :disable="isMoveDisabled"
                :loading="isValidating && isMove"
                :label="i18n.t('common.move')"
                @click="() => onNextButtonClick(true)"
              >
                <template #loading>
                  <div class="q-ph-md row">
                    <div>{{ i18n.t('common.validating') }}</div>
                    <q-spinner-dots class="on-right" />
                  </div>
                </template>
              </q-btn>
              <q-btn
                v-if="step > 1"
                style="width: 9rem"
                :color="isImporting ? 'positive' : 'primary'"
                :label="isMove ? i18n.t('common.move') : i18n.t('common.copy')"
                :loading="isImporting"
                :disable="!canImport"
                class="q-ml-sm"
                @click="onImportClick"
              >
                <template #loading>
                  <div class="q-ph-md row">
                    {{ isMove ? i18n.t('common.moving') : i18n.t('common.copying') }}
                    <q-spinner-gears class="on-right" />
                  </div>
                </template>
              </q-btn>
              <q-btn
                v-if="step > 1"
                flat
                style="width: 9rem"
                color="primary"
                label="Back"
                class="q-ml-sm"
                :disable="isImporting"
                @click="onBackClick"
              />
            </div>
          </q-stepper-navigation>
        </template>
      </q-stepper>
    </generic-popup>
  </div>
</template>
